import $ from './jqueryGlobal';

/**
 * Custom jquery-validation rules, wired into unobtrusive validation.
 *
 * Ported from boot/jqval.js. Both rules exist to mirror server-side validation
 * in the browser, so a visitor is not told about a problem only after a round
 * trip.
 */

interface UnobtrusiveValidator {
  setDefaults(defaults: Record<string, unknown>): void;
  unobtrusive: {
    adapters: {
      addSingleVal(name: string, attribute: string): void;
      addBool(name: string): void;
    };
  };
  addMethod(name: string, method: ValidationMethod, message?: unknown): void;
  format(template: string): unknown;
}

type ValidationMethod = (
  this: { optional(element: HTMLElement): boolean; elementValue(element: HTMLElement): unknown; element(element: HTMLElement): boolean },
  value: string,
  element: HTMLInputElement,
  options?: string,
) => boolean;

const validator = ($ as unknown as { validator: UnobtrusiveValidator }).validator;

/**
 * Validate when a field loses focus, not while it is being typed into.
 *
 * jquery-validation re-checks a field on every keystroke once it has gone
 * invalid, so a half-typed email address sits under a red "Ugyldig epostadresse"
 * that rewrites itself letter by letter. Nobody has finished typing an address
 * at the third character, and telling them so is noise rather than help.
 *
 * onfocusout still validates, so an error appears as soon as the visitor moves
 * on — the natural moment to judge whether a field is complete. The trade is
 * that a corrected field keeps its message until focus leaves it too, which is
 * the right way round: the feedback arrives when the input is finished.
 *
 * Set before unobtrusive validation parses on document ready, so every form
 * picks it up. This closes an item that sat open in the old project's TODO as
 * "Form validation too eager (email), while typing".
 */
validator.setDefaults({ onkeyup: false });

/**
 * "At least N inputs matching selector S must be filled."
 *
 * Backs the contact form's requirement that either phone or email is present,
 * which the server enforces through ContactFormData.Validate. Declared in markup
 * as data-val-grouprequire-options="1,.contact-group".
 */
validator.addMethod(
  'grouprequire',
  function (this: ThisParameterType<ValidationMethod>, _value, element, options) {
    if (!options) {
      return true;
    }

    const [requiredCountRaw, selector] = options.split(',');
    const requiredCount = Number.parseInt(requiredCountRaw, 10);

    if (!selector || Number.isNaN(requiredCount)) {
      return true;
    }

    const fields = $(selector, element.form ?? undefined);
    const first = fields.eq(0);

    // The cloned validator is cached on the first field so re-validating the
    // group does not build a new one per keystroke.
    //
    // $.extend, not Object.assign. They are not interchangeable here: $.extend
    // copies with for...in, which walks the prototype chain, while Object.assign
    // copies own properties only. jquery-validation puts elementValue, element
    // and the rest on $.validator.prototype, so Object.assign produced a clone
    // with none of them and this rule threw "elementValue is not a function".
    //
    // That exception escaped before the submit handler could suppress the
    // default, so the browser performed a native POST and the page reloaded —
    // which is why the modal appeared to close instantly and no AJAX request was
    // ever made. It broke all client-side validation on the form, not just this
    // rule.
    const cached = first.data('valid_req_grp') as ThisParameterType<ValidationMethod> | undefined;
    const groupValidator = cached ?? ($.extend({}, this) as ThisParameterType<ValidationMethod>);

    first.data('valid_req_grp', groupValidator);

    const filled = fields.filter(function (this: HTMLElement) {
      return Boolean(groupValidator.elementValue(this));
    }).length;

    // Re-validate siblings so filling one field clears the error on the other.
    // The guard prevents infinite recursion, since validating a sibling
    // re-enters this same rule.
    if (!$(element).data('being_validated')) {
      fields.data('being_validated', true);
      fields.each(function (this: HTMLElement) {
        groupValidator.element(this);
      });
      fields.data('being_validated', false);
    }

    return filled >= requiredCount;
  },
  validator.format('{0}'),
);

validator.unobtrusive.adapters.addSingleVal('grouprequire', 'options');

/**
 * Phone validation for the [Phone] data annotation.
 *
 * jquery-validation ships no `phone` method in its core, so without this the
 * attribute produces no client-side rule at all. Deliberately permissive: it
 * accepts international prefixes, spaces, dots, dashes and parentheses, because
 * rejecting a real number is worse than accepting an odd one the server will
 * also accept.
 */
const PHONE_PATTERN = /^[+]?[-\s./0-9()]{4,}$/;

validator.addMethod('phone', function (this: ThisParameterType<ValidationMethod>, value, element) {
  if (this.optional(element)) {
    return true;
  }

  return PHONE_PATTERN.test(value.trim());
});

validator.unobtrusive.adapters.addBool('phone');
